import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search } from 'lucide-react';
import { ACCOUNT_FILTERS, ROLE_LABELS, type AccountFilter, type AdminAccountDetail, type AdminAccountRow, type AdminLogEntry, type Page } from '@hoopify/shared';
import { api, ApiError, errorMessage } from '../../lib/api';
import { ACCOUNT_FILTER_LABELS } from '../../lib/admin';
import { formatDate, formatDateTime, timeAgo } from '../../lib/format';
import { Alert, Button, Dialog, Field, LoadingBlock, TextArea } from '../../components/ui';
import { Pager, ReasonDialog } from '../../components/admin/AdminLayout';

export function Flag({ tone, children }: { tone: 'danger' | 'warn' | 'ok' | 'muted'; children: React.ReactNode }) {
  const cls = { danger: 'bg-danger-soft text-danger', warn: 'bg-warn-soft text-warn', ok: 'bg-ok-soft text-ok', muted: 'bg-sunken text-muted' }[tone];
  return <span className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[12px] font-medium whitespace-nowrap ${cls}`}>{children}</span>;
}

export function AccountFlags({ a }: { a: Pick<AdminAccountRow, 'suspended' | 'emailConfirmed' | 'mfa'> }) {
  return (
    <span className="flex flex-wrap gap-1">
      {a.suspended && <Flag tone="danger">Suspended</Flag>}
      {!a.emailConfirmed && <Flag tone="warn">Email not confirmed</Flag>}
      {a.mfa && <Flag tone="ok">Two-step</Flag>}
    </span>
  );
}

/** Search box that updates the URL after a short pause. */
export function useListParams(defaultFilter: string) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const filter = params.get('filter') ?? defaultFilter;
  const page = Math.max(1, Number(params.get('page')) || 1);
  const [text, setText] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (text.trim() !== q) setParams((p) => {
        const n = new URLSearchParams(p);
        if (text.trim()) n.set('q', text.trim());
        else n.delete('q');
        n.delete('page');
        return n;
      }, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [text, q, setParams]);
  const set = (key: string, value: string | null) =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      if (value) n.set(key, value);
      else n.delete(key);
      if (key !== 'page') n.delete('page');
      return n;
    });
  return { q, filter, page, text, setText, set };
}

export function SearchBar({ text, setText, placeholder, filter, options, onFilter }: { text: string; setText: (v: string) => void; placeholder: string; filter: string; options: Record<string, string>; onFilter: (v: string) => void }) {
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      <label className="relative min-w-[220px] flex-1">
        <span className="sr-only">Search</span>
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" aria-hidden />
        <input className="field-input pl-9" type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} />
      </label>
      <select className="field-input w-auto" value={filter} onChange={(e) => onFilter(e.target.value)} aria-label="Show">
        {Object.entries(options).map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AdminAccountsPage() {
  const { q, filter, page, text, setText, set } = useListParams('all');
  const f = (ACCOUNT_FILTERS as readonly string[]).includes(filter) ? (filter as AccountFilter) : 'all';
  const list = useQuery({
    queryKey: ['admin', 'accounts', q, f, page],
    queryFn: () => api.get<Page<AdminAccountRow>>(`/admin/accounts?q=${encodeURIComponent(q)}&filter=${f}&page=${page}`),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <h1 className="mb-5 text-[24px] font-semibold tracking-[-0.01em]">Accounts</h1>
      <SearchBar text={text} setText={setText} placeholder="Search by name or email" filter={f} options={ACCOUNT_FILTER_LABELS} onFilter={(v) => set('filter', v === 'all' ? null : v)} />
      {list.isError && <Alert>{errorMessage(list.error)}</Alert>}
      {list.isLoading ? (
        <LoadingBlock />
      ) : list.data && (
        <>
          <p className="tnum mb-2 text-[14px] text-muted">
            {list.data.total} account{list.data.total === 1 ? '' : 's'}
          </p>
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table className="w-full min-w-[720px] text-left text-[14px]">
              <thead className="border-b border-line text-[13px] text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Teams</th>
                  <th className="px-4 py-2 font-medium">Joined</th>
                  <th className="px-4 py-2 font-medium">Last seen</th>
                  <th className="px-4 py-2 font-medium">
                    <span className="sr-only">Status</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {list.data.rows.map((a) => (
                  <tr key={a.id} className="hover:bg-sunken">
                    <td className="px-4 py-2.5">
                      <Link to={`/admin/accounts/${a.id}`} className="block font-medium hover:underline">
                        {a.name}
                      </Link>
                      <span className="text-[13px] text-muted">{a.email}</span>
                    </td>
                    <td className="tnum px-4 py-2.5">
                      {a.teamCount}
                      {a.headCoachOf > 0 && <span className="text-muted"> ({a.headCoachOf} as head)</span>}
                    </td>
                    <td className="tnum px-4 py-2.5 whitespace-nowrap text-muted">{formatDate(a.createdAt)}</td>
                    <td className="tnum px-4 py-2.5 whitespace-nowrap text-muted">{a.lastSeenAt ? timeAgo(a.lastSeenAt) : 'Never'}</td>
                    <td className="px-4 py-2.5">
                      <AccountFlags a={a} />
                    </td>
                  </tr>
                ))}
                {!list.data.rows.length && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted">
                      No accounts match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pager page={page} total={list.data.total} pageSize={list.data.pageSize} onPage={(p) => set('page', String(p))} />
        </>
      )}
    </>
  );
}

export function LogList({ rows, empty = 'Nothing yet.' }: { rows: AdminLogEntry[]; empty?: string }) {
  if (!rows.length) return <p className="text-[14px] text-muted">{empty}</p>;
  return (
    <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
      {rows.map((e) => (
        <li key={e.id} className="px-4 py-2.5">
          <p className="text-[14px]">{e.summary}</p>
          {e.reason && <p className="text-[13px] text-muted">Reason: {e.reason}</p>}
          <p className="tnum text-[12px] text-faint">{formatDateTime(e.createdAt)}</p>
        </li>
      ))}
    </ul>
  );
}

type Action = 'suspend' | 'restore' | 'mfa' | 'confirm' | 'signout' | 'delete' | null;

export function AdminAccountPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ['admin', 'account', id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<AdminAccountDetail>(`/admin/accounts/${id}`) });
  const [action, setAction] = useState<Action>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const post = (path: string, body: unknown = {}) =>
    api.post<AdminAccountDetail>(`/admin/accounts/${id}/${path}`, body).then((d) => {
      qc.setQueryData(key, d);
      void qc.invalidateQueries({ queryKey: ['admin', 'accounts'] });
      return d;
    });
  const simple = useMutation({
    mutationFn: (a: { path: string; done: string }) => post(a.path).then(() => a.done),
    onSuccess: (msg) => setNotice(msg),
  });

  if (q.isLoading) return <LoadingBlock />;
  if (!q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const a = q.data;
  const headTeams = a.teams.filter((t) => t.role === 'head_coach');

  return (
    <>
      <Link to="/admin/accounts" className="mb-4 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Accounts
      </Link>
      <div className="mb-5 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[24px] leading-tight sm:text-[28px]">{a.name}</h1>
          <p className="text-muted">{a.email}</p>
        </div>
        <AccountFlags a={a} />
      </div>

      {a.suspended && (
        <div className="mb-4">
          <Alert tone="danger">
            Suspended {a.suspendedAt && formatDate(a.suspendedAt)}. {a.suspendedReason && <>Reason: {a.suspendedReason}</>}
          </Alert>
        </div>
      )}
      {notice && (
        <div className="mb-4">
          <Alert tone="ok">{notice}</Alert>
        </div>
      )}
      {simple.isError && (
        <div className="mb-4">
          <Alert>{errorMessage(simple.error)}</Alert>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-8">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-line bg-surface p-4 text-[14px] sm:grid-cols-3">
            <Fact label="Joined">{formatDate(a.createdAt)}</Fact>
            <Fact label="Last seen">{a.lastSeenAt ? timeAgo(a.lastSeenAt) : 'Never'}</Fact>
            <Fact label="Email">{a.emailConfirmed ? 'Confirmed' : 'Not confirmed'}</Fact>
            <Fact label="Two-step sign-in">{a.mfa ? 'On' : 'Off'}</Fact>
            <Fact label="Sign-in">{a.locked ? 'Paused after wrong attempts' : 'Normal'}</Fact>
            <Fact label="Account ID">
              <span className="font-mono text-[12px] break-all">{a.id}</span>
            </Fact>
          </dl>

          <section>
            <h2 className="mb-2 text-[16px] font-semibold">Teams</h2>
            {a.teams.length ? (
              <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
                {a.teams.map((t) => (
                  <li key={t.id}>
                    <Link to={`/admin/teams/${t.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-sunken">
                      <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
                      {t.archived && <Flag tone="muted">Archived</Flag>}
                      <span className="text-[13px] text-muted">{ROLE_LABELS[t.role]}</span>
                      <span className="tnum w-24 text-right text-[13px] text-muted">
                        {t.memberCount} member{t.memberCount === 1 ? '' : 's'}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-muted">Not on any teams.</p>
            )}
          </section>

          <NoteEditor id={id} note={a.note} onSaved={(d) => qc.setQueryData(key, d)} />

          <section>
            <h2 className="mb-2 text-[16px] font-semibold">Admin history</h2>
            <LogList rows={a.log} empty="No admin actions on this account yet." />
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-lg border border-line bg-surface p-4">
            <h2 className="mb-3 font-semibold">Help with sign-in</h2>
            <div className="flex flex-col gap-2">
              <Button variant="secondary" disabled={a.suspended} loading={simple.isPending && simple.variables?.path === 'password-reset'} onClick={() => simple.mutate({ path: 'password-reset', done: `Password reset email sent to ${a.email}.` })}>
                Send password reset email
              </Button>
              {a.mfa && (
                <Button variant="secondary" onClick={() => setAction('mfa')}>
                  Turn off two-step sign-in
                </Button>
              )}
              {!a.emailConfirmed && (
                <>
                  <Button variant="secondary" loading={simple.isPending && simple.variables?.path === 'resend-confirmation'} onClick={() => simple.mutate({ path: 'resend-confirmation', done: `Confirmation email sent to ${a.email}.` })}>
                    Resend confirmation email
                  </Button>
                  <Button variant="secondary" onClick={() => setAction('confirm')}>
                    Mark email as confirmed
                  </Button>
                </>
              )}
              <Button variant="secondary" onClick={() => setAction('signout')}>
                Sign out everywhere
              </Button>
            </div>
          </section>

          <section className="rounded-lg border border-line bg-surface p-4">
            <h2 className="mb-3 font-semibold">Account</h2>
            <div className="flex flex-col gap-2">
              {a.suspended ? (
                <Button variant="secondary" onClick={() => setAction('restore')}>
                  Lift suspension
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => setAction('suspend')}>
                  Suspend account
                </Button>
              )}
              <Button variant="danger" onClick={() => setAction('delete')}>
                Delete account
              </Button>
            </div>
          </section>
        </aside>
      </div>

      <ReasonDialog open={action === 'suspend'} onClose={() => setAction(null)} title={`Suspend ${a.name}?`} confirm="Suspend" tone="danger" run={(reason) => post('suspend', { reason }).then(() => setNotice('Account suspended. They’ve been signed out and emailed.'))}>
        They’ll be signed out straight away and can’t sign in until you lift it. Their teams stay as they are. They’ll get an email with the reason.
      </ReasonDialog>
      <ReasonDialog open={action === 'restore'} onClose={() => setAction(null)} title={`Lift the suspension on ${a.name}?`} confirm="Lift suspension" run={(reason) => post('restore', { reason }).then(() => setNotice('Suspension lifted. They’ve been emailed.'))}>
        They’ll be able to sign in again, and we’ll email them to say so.
      </ReasonDialog>
      <ReasonDialog open={action === 'mfa'} onClose={() => setAction(null)} title="Turn off two-step sign-in?" confirm="Turn it off" tone="danger" run={(reason) => post('mfa-off', { reason }).then(() => setNotice('Two-step sign-in is off. They’ve been signed out and emailed.'))}>
        Only do this once you’re sure it’s really them, for example by a reply from their email address. They’ll be signed out everywhere and can sign in with just their password.
      </ReasonDialog>
      <ReasonDialog open={action === 'confirm'} onClose={() => setAction(null)} title="Mark the email as confirmed?" confirm="Mark as confirmed" run={(reason) => post('confirm-email', { reason }).then(() => setNotice('Email marked as confirmed.'))}>
        Use this if the confirmation email isn’t reaching them and you’ve checked the address is theirs.
      </ReasonDialog>
      <ReasonDialog open={action === 'signout'} onClose={() => setAction(null)} title={`Sign ${a.name} out everywhere?`} confirm="Sign out" run={(reason) => post('sign-out', { reason }).then(() => setNotice('Signed out on every device.'))}>
        Ends every session on every device. Their account is otherwise unchanged.
      </ReasonDialog>
      <DeleteDialog open={action === 'delete'} onClose={() => setAction(null)} a={a} headTeams={headTeams.filter((t) => t.memberCount > 1).map((t) => t.name)} onDeleted={() => { void qc.invalidateQueries({ queryKey: ['admin'] }); navigate('/admin/accounts', { replace: true }); }} />
    </>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[13px] text-muted">{label}</dt>
      <dd className="tnum">{children}</dd>
    </div>
  );
}

function NoteEditor({ id, note, onSaved }: { id: string; note: string | null; onSaved: (d: AdminAccountDetail) => void }) {
  const [value, setValue] = useState(note ?? '');
  useEffect(() => setValue(note ?? ''), [note]);
  const save = useMutation({ mutationFn: () => api.patch<AdminAccountDetail>(`/admin/accounts/${id}/note`, { note: value }), onSuccess: onSaved });
  const changed = value.trim() !== (note ?? '');
  return (
    <section>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-2">
        <TextArea label="Admin note" hint="Only Hoopify admins can see this. Don’t put passwords or card details here." value={value} onChange={(e) => setValue(e.target.value)} rows={3} />
        {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
        <Button type="submit" variant="secondary" loading={save.isPending} disabled={!changed}>
          {save.isSuccess && !changed ? 'Saved' : 'Save note'}
        </Button>
      </form>
    </section>
  );
}

function DeleteDialog({ open, onClose, a, headTeams, onDeleted }: { open: boolean; onClose: () => void; a: AdminAccountDetail; headTeams: string[]; onDeleted: () => void }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const del = useMutation({ mutationFn: () => api.post(`/admin/accounts/${a.id}/delete`, { confirmEmail, reason }), onSuccess: onDeleted });
  const fields = del.error instanceof ApiError ? del.error.fields : {};
  const close = () => {
    del.reset();
    setConfirmEmail('');
    setReason('');
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} title={`Delete ${a.name}?`}>
      {headTeams.length ? (
        <>
          <p className="text-muted">
            They’re head coach of {headTeams.length === 1 ? 'a team' : 'teams'} with other people on {headTeams.length === 1 ? 'it' : 'them'}: <span className="font-medium text-ink">{headTeams.join(', ')}</span>. Move {headTeams.length === 1 ? 'it' : 'them'} to another head coach first, from the team’s page.
          </p>
          <div className="mt-5 flex justify-end">
            <Button variant="secondary" onClick={close}>
              OK
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); del.mutate(); }} className="space-y-4" noValidate>
          <div className="space-y-2 text-muted">
            <p>This can’t be undone. It deletes their account, name, email, drills and templates.</p>
            <p>Sessions they planned stay with their teams. Teams where they’re the only person are archived, so you can move them to another coach later.</p>
          </div>
          {del.isError && !Object.keys(fields).length && <Alert>{errorMessage(del.error)}</Alert>}
          <Field label={`Type ${a.email} to confirm`} value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} error={fields.confirmEmail} autoComplete="off" spellCheck={false} />
          <TextArea label="Reason" hint="Kept in the admin log. Their name and email aren’t." value={reason} onChange={(e) => setReason(e.target.value)} error={fields.reason} rows={2} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" loading={del.isPending} disabled={confirmEmail.trim().toLowerCase() !== a.email || reason.trim().length < 3}>
              Delete account
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
